import { createAsyncThunk } from '@reduxjs/toolkit';
import { PaymentMethod } from '@wix/cashier-common/dist/src/enums/payments/PaymentMethod';
import { t } from '../../utils/i18n';
import { type BiLogger } from '../../components/Provider/BiLogger/BiLogger';
import { selectGooglePayPaymentData, selectTopProps } from '../../selectors';
import { getDisplayItems } from '../../components/payment-methods/GooglePay/utils/getTransactionInfo';
import { type GooglePayButtonConfig } from '../../components/payment-methods/GooglePay/types';
import { type StoreState } from '../../types/StoreState';
import { amountToLong } from '../../utils/amountToLong';
import { UNEXPECTED_ERROR } from '../../types/Shipping';
import { spbShippingMethodChanged, spbShippingMethodNotAccepted } from '../../utils/bi/events/biEvents';
import { selectGooglePayMetadata } from '../../selectors/domain';

export type SelectionOption = Parameters<GooglePayButtonConfig['onPaymentDataChanged']>[0]['shippingOptionData'];

type OnShippingMethodSelectedParams = {
  transactionInfo: google.payments.api.TransactionInfo;
  biLogger: BiLogger;
  selectionOption: SelectionOption;
};

export type OnShippingMethodSelectedReturn = ReturnType<GooglePayButtonConfig['onPaymentDataChanged']>;

export const onShippingMethodSelected = createAsyncThunk<
  OnShippingMethodSelectedReturn,
  OnShippingMethodSelectedParams,
  {
    state: StoreState;
  }
>('GOOGLE_ON_SHIPPING_METHOD_SELECTED', async ({ transactionInfo, selectionOption, biLogger }, thunkAPI) => {
  const state = thunkAPI.getState();
  const topProps = selectTopProps(state);
  const { shippingMethods } = selectGooglePayPaymentData(state);
  const methodMetadata = selectGooglePayMetadata(state);

  const selectedMethod = shippingMethods.find((method) => method.identifier === selectionOption.id);

  try {
    const updates = await topProps.onShippingMethodSelected(selectedMethod);

    biLogger.report(spbShippingMethodChanged, {
      fieldChanged: 'shippingMethod',
      amountLong: amountToLong(transactionInfo.totalPrice),
      appInstanceId: topProps.meta.appInstanceId,
      currency: transactionInfo.currencyCode,
      externalOrderId: transactionInfo.transactionId,
      paymentMethod: PaymentMethod.GooglePay,
      paymentProvider: methodMetadata.providerId,
      isPartner: undefined as any, // TODO[bi]
      amount: undefined as any, // TODO[bi]
      orderSnapshotId: undefined as any, // TODO[bi]
      transactionId: undefined as any, // TODO[bi]
    });

    return {
      newTransactionInfo: {
        ...transactionInfo,
        totalPrice: updates.paymentAmount,
        ...(updates.paymentBreakdown && {
          displayItems: getDisplayItems({
            paymentBreakdown: updates.paymentBreakdown,
            requestShipping: topProps.requestShipping,
          }),
        }),
      },
    };
  } catch (error) {
    biLogger.report(spbShippingMethodNotAccepted, {
      fieldChanged: 'shippingMethod',
      appInstanceId: topProps.meta.appInstanceId,
      currency: transactionInfo.currencyCode,
      externalOrderId: transactionInfo.transactionId,
      paymentMethod: PaymentMethod.GooglePay,
      paymentProvider: methodMetadata.providerId,
      errorReason: `${UNEXPECTED_ERROR}: ${typeof error === 'string' ? error : JSON.stringify(error)}`,
      amountLong: undefined as any, // TODO[bi]
      isPartner: undefined as any, // TODO[bi]
      amount: undefined as any, // TODO[bi]
      orderSnapshotId: undefined as any, // TODO[bi]
      transactionId: undefined as any, // TODO[bi]
    });

    return {
      error: {
        reason: 'OTHER_ERROR',
        intent: 'SHIPPING_OPTION',
        message: t('pm.shipping.optionChange.unknownError'),
      },
    };
  }
});
